import React, { useCallback, useState, useEffect } from 'react';
import { KnowledgeModule } from '../../types';
import { getKnowledgeModules, deleteKnowledgeModule } from '../../services/marketingApi';
import ModuleRow from './ModuleRow';
import ModuleEditor from './ModuleEditor';
import { Empty, Loading, PageFrame, Section, Th, btn, btnTone } from '../../components/Kit';

const reason = (err: any): string => err?.response?.data?.detail || err?.message || String(err);

const KnowledgeBase: React.FC = () => {
  const [modules, setModules] = useState<KnowledgeModule[]>([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editingModule, setEditingModule] = useState<KnowledgeModule | null>(null);
  const [error, setError] = useState<string | null>(null);


  const fetchModules = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getKnowledgeModules();
      setModules(response.modules);
      setError(null);
    } catch (err) {
      setError(`Couldn't load modules: ${reason(err)}`);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchModules();
  }, [fetchModules]);

  const handleCreate = () => {
    setEditingModule(null);
    setShowEditor(true);
  };

  const handleEdit = (module: KnowledgeModule) => {
    setEditingModule(module);
    setShowEditor(true);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this module? This cannot be undone.')) return;
    try {
      await deleteKnowledgeModule(id);
      fetchModules();
    } catch (err) {
      setError(`Couldn't delete the module: ${reason(err)}`);
    }
  };

  const handleEditorClose = () => {
    setShowEditor(false);
    setEditingModule(null);
  };

  const handleEditorSuccess = () => {
    handleEditorClose();
    fetchModules();
  };

  return (
    <PageFrame
      title="Knowledge Base"
      subline="What the AI agent knows and shares."
      action={<button type="button" onClick={handleCreate} className={`${btn} ${btnTone.forest}`}>+ New module</button>}
    >
      {error && <div className="rounded-lg bg-brick/5 text-brick px-4 py-3 text-[13.5px]">{error}</div>}
      {loading ? <Loading what="modules" /> : (
        <Section title="Modules" note={`${modules.length} total`}>
          <table className="w-full border-collapse">
            <thead><tr>
              <Th>#</Th><Th left>Module</Th><Th left>Media</Th><Th left>Description</Th><Th />
            </tr></thead>
            <tbody>
              {modules.length === 0 && <Empty cols={5}>No modules yet.</Empty>}
              {modules.map((module) => (
                <ModuleRow key={module.id} module={module} onEdit={handleEdit} onDelete={handleDelete} />
              ))}
            </tbody>
          </table>
        </Section>
      )}

      {showEditor && (
        <ModuleEditor module={editingModule} onClose={handleEditorClose} onSuccess={handleEditorSuccess} />
      )}
    </PageFrame>
  );
};

export default KnowledgeBase;
